import { useId, useMemo, useState } from 'react';
import { DeleteOutlined, PauseCircleOutlined, PlayCircleOutlined, StarOutlined, WarningOutlined } from '@ant-design/icons';

import { MenuThaoTac } from '../../../components/shared';
import { Badge, Button, Field, Input, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { sinhMa } from '../../../lib/ma-tu-sinh';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { MA_TOI_DA, NHIP_XEM_TRUOC_MS, QUYEN, TIEN_TO_MA_QUY_TRINH } from '../constants';
import { useGhiQuyTrinh, useGianNhip, useXemTruocQuyTrinh } from '../hooks/useCauHinhEkyc';
import { datBuoc, giongNhau, loiKhongCoO, loiTheoTruong, loiTuApi } from '../nghiep-vu/cau-hinh';
import type { ChinhSach, DanhMucQuyTrinh, QuyTrinh, VanDeCauHinh } from '../types';
import { FormChinhSach, coOChinhSach } from './FormChinhSach';
import { KhoiBuocQuyTrinh } from './KhoiBuocQuyTrinh';

interface BanNhap {
  ten: string;
  moTa: string;
  buoc: Record<string, boolean>;
  chinhSach: ChinhSach;
}

/** Ô có trên màn — lỗi có con trỏ khác thì gom vào dải chung, không rơi mất. */
const coO = (truong: string) =>
  truong === '#/ten' || truong === '#/ma' || truong === '#/moTa' || truong.startsWith('#/buoc/') || coOChinhSach(truong);

/**
 * Trình sửa một quy trình — hoặc quy trình mới khi `quyTrinh` là `null`.
 *
 * ## Khoá lạc quan giữ ở đây, không đọc lại từ danh sách
 *
 * `phienBanGoc` là số phiên bản **lúc bắt đầu sửa**, chỉ đổi khi chính màn này lưu xong. Đọc số từ danh sách (danh
 * sách tự làm mới khi quay lại tab) thì lượt lưu luôn mang số mới nhất — và ghi đè lặng lẽ lượt lưu của người khác,
 * đúng thứ khoá lạc quan sinh ra để chặn. Bị 409 thì người dùng tự bấm "Tải bản mới nhất" và biết mình bỏ gì.
 *
 * ## Lỗi đến từ máy chủ
 *
 * Mọi đổi thay đi qua xem trước phía server (giãn nhịp 400 ms); lỗi đặt cạnh đúng ô theo con trỏ, cảnh báo gom
 * thành một khối "Lưu ý" — cảnh báo không chặn lưu (`05` §7.2).
 */
export function TrinhSuaQuyTrinh({
  ungDungId,
  quyTrinh,
  danhMuc,
  onDaTao,
  onTaiLai,
  onDaXoa,
}: {
  ungDungId: string;
  quyTrinh: QuyTrinh | null;
  danhMuc: DanhMucQuyTrinh;
  onDaTao: (q: QuyTrinh) => void;
  /** Bỏ bản nháp, đọc lại bản mới nhất của máy chủ (sau 409 `EKYC_WORKFLOW_STALE`). */
  onTaiLai: () => void;
  onDaXoa: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const formId = useId();
  const quyen = useModulePermissions(QUYEN);
  const ghi = useGhiQuyTrinh(ungDungId);

  const moi = quyTrinh === null;
  const goc: BanNhap = quyTrinh
    ? { ten: quyTrinh.ten, moTa: quyTrinh.moTa, buoc: quyTrinh.steps, chinhSach: quyTrinh.chinhSach }
    : { ten: '', moTa: '', buoc: {}, chinhSach: danhMuc.chinhSachMacDinh };
  const [nhap, setNhap] = useState<BanNhap>(goc);
  const [daLuu, setDaLuu] = useState<BanNhap>(goc);
  const [phienBanGoc, setPhienBanGoc] = useState(quyTrinh?.phienBan ?? 0);
  const [loiLuu, setLoiLuu] = useState<{ loi: ApiError; truong: VanDeCauHinh[] } | null>(null);
  const [hoiXoa, setHoiXoa] = useState(false);

  const suaDuoc = moi ? quyen.quyTrinhTao : quyen.quyTrinhSua;
  const coThayDoi = !giongNhau(nhap, daLuu);
  const ma = moi ? sinhMa(nhap.ten, { tienTo: TIEN_TO_MA_QUY_TRINH, toiDa: MA_TOI_DA }) : quyTrinh.ma;

  // Ổn định tham chiếu: một đối tượng mới mỗi lần render làm bộ giãn nhịp đặt lại mãi và không bao giờ hỏi.
  const cauHinh = useMemo(() => ({ buoc: nhap.buoc, chinhSach: nhap.chinhSach }), [nhap.buoc, nhap.chinhSach]);
  const tre = useGianNhip(cauHinh, NHIP_XEM_TRUOC_MS);
  const xemTruoc = useXemTruocQuyTrinh(tre.buoc, tre.chinhSach);
  const vanDe = [...(xemTruoc.data?.vanDe ?? quyTrinh?.vanDe ?? []), ...(loiLuu?.truong ?? [])];
  const loi = loiTheoTruong(vanDe);
  const canhBao = vanDe.filter((v) => v.muc === 'canh_bao');
  const loiChung = loiKhongCoO(vanDe, coO);
  const coLoi = vanDe.some((v) => v.muc === 'loi');
  const loiTen = loi['#/ten'] ?? loi['#/ma'] ?? (moi && nhap.ten.trim() && !ma ? t('ekyc.quyTrinh.tenKhongSinhMa') : undefined);
  const biCu = loiLuu?.loi.code === 'EKYC_WORKFLOW_STALE';

  const doi = (p: Partial<BanNhap>) => {
    setNhap((cu) => ({ ...cu, ...p }));
    setLoiLuu(null);
  };

  const sauLuu = (q: QuyTrinh) => {
    const ban: BanNhap = { ten: q.ten, moTa: q.moTa, buoc: q.steps, chinhSach: q.chinhSach };
    setNhap(ban);
    setDaLuu(ban);
    setPhienBanGoc(q.phienBan);
  };
  const khiLoi = (e: ApiError) => setLoiLuu({ loi: e, truong: loiTuApi(e.details) });

  const luu = () => {
    if (!nhap.ten.trim() || !ma || ghi.tao.isPending || ghi.sua.isPending) return;
    if (moi) {
      ghi.tao.mutate(
        { ungDungId, ma, ten: nhap.ten.trim(), moTa: nhap.moTa.trim(), buoc: nhap.buoc, chinhSach: nhap.chinhSach },
        { onSuccess: (q) => { toast.success(t('ekyc.quyTrinh.daTao')); onDaTao(q); }, onError: khiLoi },
      );
    } else {
      ghi.sua.mutate(
        { id: quyTrinh.id, du: { phienBan: phienBanGoc, ten: nhap.ten.trim(), moTa: nhap.moTa.trim(), buoc: nhap.buoc,
          chinhSach: nhap.chinhSach } },
        { onSuccess: (q) => { sauLuu(q); toast.success(t('ekyc.quyTrinh.daLuu')); }, onError: khiLoi },
      );
    }
  };

  const batTat = (bat: boolean) => {
    if (!quyTrinh) return;
    ghi.sua.mutate({ id: quyTrinh.id, du: { phienBan: phienBanGoc, dangBat: bat } }, {
      onSuccess: (q) => {
        setPhienBanGoc(q.phienBan);
        toast.success(t(bat ? 'ekyc.quyTrinh.daBat' : 'ekyc.quyTrinh.daTat'));
      },
      onError: khiLoi,
    });
  };

  return (
    <div className="space-y-4">
      {!moi && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {quyTrinh.macDinh && <Badge variant="primary">{t('ekyc.quyTrinh.macDinh')}</Badge>}
            {!quyTrinh.dangBat && <Badge variant="neutral">{t('ekyc.quyTrinh.dangTat')}</Badge>}
            <span className="text-xs text-content-muted">{t('ekyc.quyTrinh.phienBan', { so: phienBanGoc })}</span>
          </div>
          <MenuThaoTac
            tenBanGhi={quyTrinh.ten}
            muc={[
              {
                khoa: 'mac-dinh', nhan: t('ekyc.quyTrinh.datMacDinh'), icon: <StarOutlined />,
                hien: quyen.quyTrinhSua && !quyTrinh.macDinh && quyTrinh.dangBat,
                onChon: () => ghi.datMacDinh.mutate(quyTrinh.id, {
                  onSuccess: () => toast.success(t('ekyc.quyTrinh.daDatMacDinh', { ten: quyTrinh.ten })),
                  onError: (e) => toast.error(dichLoi(e)),
                }),
              },
              {
                khoa: 'tat', nhan: t('ekyc.quyTrinh.tatQuyTrinh'), icon: <PauseCircleOutlined />, nhom: 'sua',
                moTa: t('ekyc.quyTrinh.tatQuyTrinhMo'),
                hien: quyen.quyTrinhSua && quyTrinh.dangBat && !quyTrinh.macDinh,
                onChon: () => batTat(false),
              },
              {
                khoa: 'bat', nhan: t('ekyc.quyTrinh.batQuyTrinh'), icon: <PlayCircleOutlined />, nhom: 'sua',
                hien: quyen.quyTrinhSua && !quyTrinh.dangBat,
                onChon: () => batTat(true),
              },
              {
                khoa: 'xoa', nhan: t('ekyc.quyTrinh.xoaQuyTrinh'), icon: <DeleteOutlined />, nhom: 'pha-huy',
                hien: quyen.quyTrinhXoa && !quyTrinh.macDinh,
                onChon: () => setHoiXoa(true),
              },
            ]}
          />
        </div>
      )}

      <form
        id={formId}
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          luu();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('ekyc.quyTrinh.ten')} required error={loiTen}>
            {(p) => (
              <Input
                {...p}
                value={nhap.ten}
                maxLength={200}
                disabled={!suaDuoc}
                status={p['aria-invalid'] ? 'error' : undefined}
                onChange={(e) => doi({ ten: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('ekyc.quyTrinh.ma')} hint={moi ? t('ekyc.quyTrinh.maTuSinh') : t('ekyc.quyTrinh.maKhongDoi')}>
            {(p) => <Input {...p} readOnly value={ma} className="font-mono" placeholder={t('ekyc.quyTrinh.maChoTen')} />}
          </Field>
        </div>
        <Field label={t('ekyc.quyTrinh.moTa')} error={loi['#/moTa']}>
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={2000}
              disabled={!suaDuoc}
              value={nhap.moTa}
              onChange={(e) => doi({ moTa: e.target.value })}
            />
          )}
        </Field>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-content-primary">{t('ekyc.quyTrinh.cacBuoc')}</h2>
          <KhoiBuocQuyTrinh
            buoc={xemTruoc.data?.effectiveSteps ?? quyTrinh?.effectiveSteps ?? null}
            danhMuc={danhMuc.buoc}
            nhom={danhMuc.nhom}
            suaDuoc={suaDuoc}
            loi={loi}
            onDoi={(m, bat) => doi({ buoc: datBuoc(nhap.buoc, m, bat) })}
          />
          <p className="text-xs text-content-muted">{t('ekyc.quyTrinh.thuTuMayChu')}</p>
        </section>

        {canhBao.length > 0 && (
          <div role="note" className="space-y-1 rounded border border-warning-200 bg-warning-50 px-3 py-2.5">
            <p className="flex items-center gap-2 text-sm font-medium text-warning-700">
              <WarningOutlined /> {t('ekyc.quyTrinh.luuY')}
            </p>
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-warning-700">
              {canhBao.map((v) => (
                <li key={`${v.ma}-${v.truong}`}>{v.thongDiep}</li>
              ))}
            </ul>
          </div>
        )}

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-content-primary">{t('ekyc.quyTrinh.chinhSach')}</h2>
          <FormChinhSach
            cs={nhap.chinhSach}
            luaChon={danhMuc.luaChon}
            suaDuoc={suaDuoc}
            loi={loi}
            onDoi={(cs) => doi({ chinhSach: cs })}
          />
        </section>
      </form>

      {suaDuoc && (
        <div className="sticky bottom-0 space-y-2 border-t border-default bg-surface py-3">
          {biCu ? (
            <LoiTrongHop>
              {t('ekyc.quyTrinh.biCu')}{' '}
              <Button variant="link" size="sm" onClick={onTaiLai}>
                {t('ekyc.quyTrinh.taiBanMoi')}
              </Button>
            </LoiTrongHop>
          ) : (
            (loiChung.length > 0 || (loiLuu && loiLuu.truong.length === 0)) && (
              <LoiTrongHop>
                {loiLuu && loiLuu.truong.length === 0 && <span className="block">{dichLoi(loiLuu.loi)}</span>}
                {loiChung.map((v) => (
                  <span key={v.truong} className="block">
                    <span className="font-mono">{v.truong}</span>: {v.thongDiep}
                  </span>
                ))}
              </LoiTrongHop>
            )
          )}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {coLoi && <span className="text-xs text-danger-700">{t('ekyc.quyTrinh.conLoi')}</span>}
            {!moi && coThayDoi && (
              <Button variant="outline" size="md" onClick={() => { setNhap(daLuu); setLoiLuu(null); }}>
                {t('ekyc.quyTrinh.boThayDoi')}
              </Button>
            )}
            <Button
              variant="primary"
              size="md"
              htmlType="submit"
              form={formId}
              disabled={(!moi && !coThayDoi) || !nhap.ten.trim() || !ma}
              loading={ghi.tao.isPending || ghi.sua.isPending}
            >
              {moi
                ? ghi.tao.isPending ? t('ekyc.quyTrinh.dangTao') : t('ekyc.quyTrinh.taoQuyTrinh')
                : ghi.sua.isPending ? t('chung.dangLuu') : t('ekyc.quyTrinh.luuQuyTrinh')}
            </Button>
          </div>
        </div>
      )}

      {quyTrinh && (
        <HopXacNhanThaoTac
          open={hoiXoa}
          tieuDe={t('ekyc.quyTrinh.xoaHoi', { ten: quyTrinh.ten })}
          than={t('ekyc.quyTrinh.xoaThan')}
          nhanXacNhan={t('ekyc.quyTrinh.xoaQuyTrinh')}
          nguyHiem
          dangChay={ghi.xoa.isPending}
          loi={ghi.xoa.error ? dichLoi(ghi.xoa.error) : null}
          onClose={() => { setHoiXoa(false); ghi.xoa.reset(); }}
          onXacNhan={() => ghi.xoa.mutate(quyTrinh.id, {
            onSuccess: () => {
              setHoiXoa(false);
              toast.success(t('ekyc.quyTrinh.daXoa'));
              onDaXoa();
            },
          })}
        />
      )}
    </div>
  );
}
